import * as Types from './typedef.js';
/** @typedef {Types.Sampler1Channel} Sampler1Channel */
/** @typedef {Types.PixelCoordinate} PixelCoordinate */

import { applyGaussianBlur } from './blurringFunctions/GaussionBlur.js';
import { manhattanNeighbors } from './distanceFunctions/index.js';

export { manhattanDistance } from './distanceFunctions/index.js';

/**
 * @class
 * @classdesc Image4Channel is a class that contains the image data and its width and height (actually same as ImageData), just a wrapper around Uint8ClampedArray
 */
export class Image4Channel {
  /**@type {Uint8ClampedArray} */
  #data;
  /**@type {number} */
  #width;
  /**@type {number} */
  #height;

  /**
   * Create a new Image4Channel object
   * @param {Uint8ClampedArray} data - The image data
   * @param {number} width - The image width
   * @param {number} height - The image height
   * @throws {Error} If the data length does not match the width and height
   */
  constructor(data, width, height) {
    if (data.length < width * height * 4) {
      throw new Error(
        'The data length does not match the width and height, this is not an image data',
      );
    }
    this.#data = data;
    this.#width = width;
    this.#height = height;
  }

  /**
   * Create a new PreprocesserImageData object from an ImageData object
   * @param {ImageData} imageData - The image data
   */
  static fromImageData(imageData) {
    return new Image4Channel(imageData.data, imageData.width, imageData.height);
  }

  /**
   * Create a new Image4Channel object from an Image1Channel object
   * @param {Image1Channel} image1Channel - The image 1 channel data
   */
  static fromImage1Channel(image1Channel) {
    const greyScale = image1Channel.data;
    const data = new Uint8ClampedArray(image1Channel.numPixels * 4);
    for (let i = 0; i < image1Channel.numPixels; i++) {
      data[i * 4] = greyScale[i];
      data[i * 4 + 1] = greyScale[i];
      data[i * 4 + 2] = greyScale[i];
      data[i * 4 + 3] = 255;
    }
    return new Image4Channel(data, image1Channel.width, image1Channel.height);
  }

  /**
   * Create a new Image4Channel object by blurringFunctions
   * @param {number} sigma - The sigma value for the gaussian blur
   */
  gaussianBlur(sigma) {
    const data = applyGaussianBlur(
      this.#data,
      this.#width,
      this.#height,
      5,
      sigma,
    );
    return new Image4Channel(data, this.#width, this.#height);
  }

  /**
   * Convert the image to grey scale
   * @return {Image1Channel} The single channel grey scale image data
   */
  toGreyScale() {
    return Image1Channel.fromImage4Channel(this);
  }

  /**
   * Convert the image to binary image (standard binarization)
   * @return {Image1Channel} The single channel binary image data
   */
  toBinarize() {
    const greyScale = this.toGreyScale();
    const binarizeFilter = createBinaryFilter(24, 16);
    return binarizeFilter(greyScale);
  }

  /**
   * Get the image data
   * @return {Uint8ClampedArray} The image data
   */
  get data() {
    return this.#data;
  }

  /**
   * Get the image width
   * @return {number} The image width
   */
  get width() {
    return this.#width;
  }

  /**
   * Get the image height
   * @return {number} The image height
   */
  get height() {
    return this.#height;
  }

  /**
   * Get the number of pixels
   * @return {number} The number of pixels
   */
  get numPixels() {
    return this.#width * this.#height;
  }
}

/**
 * @class
 * @classdesc Image1Channel is a class that contains the image data and its width and height, can be converted from Image4Channel
 */
export class Image1Channel {
  /**@type {Uint8ClampedArray} */
  #data;
  /**@type {number} */
  #width;
  /**@type {number} */
  #height;

  // @ts-expect-error TS-style JSDoc duplication warning
  /**
   * Create a new Image1Channel object
   * @param {Uint8ClampedArray} data - The image data
   * @param {number} width - The image width
   * @param {number} height - The image height
   * @throws {Error} If the data length does not match the width and height
   */
  constructor(data, width, height) {
    if (data.length < width * height) {
      throw new Error(
        'The data length does not match the width and height, this is not single channel image data',
      );
    }
    this.#data = data;
    this.#width = width;
    this.#height = height;
  }

  /**
   * Create a new PreprocesserImageData object from an ImageData object
   * @param {Image4Channel} image4Channel - The image 4 channel data
   */
  static fromImage4Channel(image4Channel) {
    const greyscale = greyScaleFrom4Channel(image4Channel);
    return new Image1Channel(
      greyscale,
      image4Channel.width,
      image4Channel.height,
    );
  }

  /**
   * create a new Image4Channel object from a sampling function and source
   * @param {Sampler1Channel} sampler - The sampling function
   * @param {Image1Channel} source - The source image data
   * @param {number} width - The width of the new image
   * @param {number} height - The height of the new image
   * @returns {Image1Channel} The result image data
   */
  static fromSampler(sampler, source, width, height) {
    const data = new Uint8ClampedArray(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // get the center of the pixel
        const valX = x + 0.5;
        const valY = y + 0.5;

        const pixelCoordinate = sampler(valX, valY);
        data[y * width + x] = source.get(
          Math.floor(pixelCoordinate.x),
          Math.floor(pixelCoordinate.y),
        )
          ? 255
          : 0;
      }
    }
    return new Image1Channel(data, width, height);
  }

  /**
   * Create a new Image1Channel object by cropping the source image
   * @param {number} x - The starting x value
   * @param {number} y - The starting y value
   * @param {number} width - The width of the cropped image
   * @param {number} height - The height of the cropped image
   * @returns {Image1Channel} The cropped image data
   */
  cropped(x, y, width, height) {
    const data = new Uint8ClampedArray(width * height);
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        data[i * width + j] = this.#data[x + j + (y + i) * this.#width];
      }
    }

    return new Image1Channel(data, width, height);
  }

  /**
   * create a new Image4Channel object from a sampling function and source
   * @param {Sampler1Channel} sampler - The sampling function
   * @param {Image1Channel} source - The source image data
   * @param {number} width - The width of the new image
   * @param {number} height - The height of the new image
   * @returns {Image1Channel} The result image data
   */
  static fromExtendedSampler(
    sampler,
    source,
    width,
    height,
    extendedDimension,
  ) {
    const data = new Uint8ClampedArray(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // get the center of the pixel
        const valX = x + 0.5;
        const valY = y + 0.5;

        const pixelCoordinate = sampler(
          valX - extendedDimension,
          valY - extendedDimension,
        );
        data[y * width + x] = source.get(
          Math.floor(pixelCoordinate.x),
          Math.floor(pixelCoordinate.y),
        )
          ? 255
          : 0;
      }
    }
    return new Image1Channel(data, width, height);
  }

  /**
   * Create a new SquareMatrix object from a sampling function
   * @param {Sampler1Channel} sampler - The sampling function
   * @param {number} width - The result width of the square matrix
   * @returns {SquareMatrix} The result square matrix
   */
  sampleToSquareMatrix(sampler, width) {
    const data = new Uint8ClampedArray(width * width);
    for (let y = 0; y < width; y++) {
      for (let x = 0; x < width; x++) {
        // get the center of the pixel
        const valX = x + 0.5;
        const valY = y + 0.5;

        const pixelCoordinate = sampler(valX, valY);
        data[y * width + x] = this.get(
          Math.floor(pixelCoordinate.x),
          Math.floor(pixelCoordinate.y),
        )
          ? 255
          : 0;
      }
    }
    return new SquareMatrix(data, width);
  }

  /**
   * Create a new SquareMatrix object from a sampling function
   * @param {Sampler1Channel} sampler - The sampling function
   * @param {number} width - The result width of the square matrix
   * @returns {SquareMatrix} The result square matrix
   */
  overSampleToSquareMatrix(sampler, width) {
    const data = new Uint8ClampedArray(width * width);
    for (let y = 0; y < width; y++) {
      for (let x = 0; x < width; x++) {
        // get the center of the pixel
        const valX = x + 0.5;
        const valY = y + 0.5;

        const pixelCoordinate = sampler(valX, valY);
        // do a oversampling here
        // take 9 pixels and then do a majority vote
        let voteCount = 0;
        for (let i = -1; i < 2; i++) {
          for (let j = -1; j < 2; j++) {
            if (
              this.get(
                Math.floor(pixelCoordinate.x) + i,
                Math.floor(pixelCoordinate.y) + j,
              )
            ) {
              voteCount++;
            }
          }
        }
        data[y * width + x] = voteCount >= 5 ? 255 : 0;
      }
    }
    return new SquareMatrix(data, width);
  }

  /**
   * Create a new SquareMatrix object from a sampling function and source
   * @param {Sampler1Channel} sampler - The sampling function
   * @param {number} width - The result width of the square matrix
   * @returns {SquareMatrix} The result square matrix
   */
  extendSampleToSquareMatrix(sampler, width, extendedDimension) {
    const data = new Uint8ClampedArray(width * width);
    for (let y = 0; y < width; y++) {
      for (let x = 0; x < width; x++) {
        // get the center of the pixel
        const valX = x + 0.5;
        const valY = y + 0.5;

        const pixelCoordinate = sampler(
          valX - extendedDimension,
          valY - extendedDimension,
        );
        data[y * width + x] = this.get(
          Math.floor(pixelCoordinate.x),
          Math.floor(pixelCoordinate.y),
        )
          ? 255
          : 0;
      }
    }
    return new SquareMatrix(data, width);
  }

  /**
   * Convert the image to 4 channel image
   * @return {Image4Channel} The 4 channel image data
   */
  toImage4Channel() {
    return Image4Channel.fromImage1Channel(this);
  }

  /**
   * create inverted image
   * @returns {Image1Channel} The inverted image data
   */
  invert() {
    return new Image1Channel(
      this.data.map((val) => 255 - val),
      this.width,
      this.height,
    );
  }

  /**
   * Get the value of a pixel
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @returns {boolean} The value of the pixel
   */
  get(x, y) {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return false;
    }
    return !!this.data[y * this.width + x];
  }

  /**
   * Get the image data
   * @return {Uint8ClampedArray} The image data
   */
  get data() {
    return this.#data;
  }

  /**
   * Get the image width
   * @return {number} The image width
   */
  get width() {
    return this.#width;
  }

  /**
   * Get the image height
   * @return {number} The image height
   */
  get height() {
    return this.#height;
  }

  /**
   * Get the number of pixels
   * @return {number} The number of pixels
   */
  get numPixels() {
    return this.#width * this.#height;
  }
}

/**
 * Although this class has a lot of similarity with Image4Channel and Image1Channel, but I think it should be treated completely as different entity, it has no height though...
 * @class
 * @classdesc SquareMatrix is a class that contains extracted bits from image1Channel, it is a square matrix, so it has no height
 *
 */
export class SquareMatrix {
  /**@type {Uint8ClampedArray} */
  #data;
  /**@type {number} */
  #width;

  /**
   * Create a new SquareMatrix
   * @param {Uint8ClampedArray} data - The image data
   * @param {number} width - The image width
   * @throws {Error} If the data length does not match the width and height
   */
  constructor(data, width) {
    if (data.length < width * width) {
      throw new Error(
        'The data length does not match the width and height, this is not single channel image data',
      );
    }
    this.#data = data;
    this.#width = width;
  }

  /**
   * Get the image data
   * @return {Uint8ClampedArray} The image data
   */
  get data() {
    return this.#data;
  }

  /**
   * Get the image width
   * @return {number} The image width
   */
  get width() {
    return this.#width;
  }

  /**
   * Get the image width
   * @return {number} The image width
   */
  get height() {
    return this.#width;
  }

  /**
   * Get the value of a pixel
   * @param {number} x - The x coordinate
   * @param {number} y - The y coordinate
   * @returns {boolean} The value of the pixel
   */
  get(x, y) {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return false;
    }
    return !!this.data[y * this.width + x];
  }

  /**
   * Create a new squareMatrix flipped on the x = y axis
   * @returns {SquareMatrix} The flipped square matrix
   */
  flipped() {
    const data = new Uint8ClampedArray(this.width * this.width);
    for (let y = 0; y < this.width; y++) {
      for (let x = 0; x < this.width; x++) {
        data[y * this.width + x] = this.get(y, x);
      }
    }

    return new SquareMatrix(data, this.width);
  }
  /**
   * Create a new squareMatrix with black and white inverted
   * @returns {SquareMatrix} The inverted sqaure matrix
   */
  inverted() {
    const data = this.#data.map((val) => (!!val ? 0 : 255));
    return new SquareMatrix(data, this.width);
  }

  /**
   * Convert the squareMatrix to 4 channel image
   * @returns {Image4Channel} The 4 channel image data
   */
  toImage4Channel() {
    const data = new Uint8ClampedArray(this.width * this.width * 4);
    for (let i = 0; i < this.width * this.width; i++) {
      data[i * 4] = this.data[i];
      data[i * 4 + 1] = this.data[i];
      data[i * 4 + 2] = this.data[i];
      data[i * 4 + 3] = 255;
    }
    return new Image4Channel(data, this.width, this.width);
  }
}

/**
 * convert image to grey scale
 * @param {Image4Channel} image4Channel - The image data
 * @return {Image4Channel} The single channel grey scale image data
 */
function greyScaleFrom4Channel(image4Channel) {
  const data = image4Channel.data;
  const numPixels = image4Channel.numPixels;

  const greyScale = new Uint8ClampedArray(numPixels);
  for (let i = 0; i < numPixels; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    greyScale[i] = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  return greyScale;
}

/**
 * Otsu's method is used to find the threshold value for binarization
 * @param {number[]} histogram - The histogram of the image
 * @returns {number} The threshold value
 */
const otsuThreshold = (histogram) => {
  const total = histogram.reduce((acc, val) => acc + val, 0);
  let sum = 0;
  let peakValues = [];

  for (let i = 0; i < 256; i++) {
    if (histogram[i] > 0) {
      peakValues.push(i);
    }
    sum += i * histogram[i];
  }

  // Edge case: If there are exactly 2 peaks, use the midpoint
  if (peakValues.length === 2) {
    return Math.floor((peakValues[0] + peakValues[1]) / 2);
  }

  let sumB = 0;
  let wB = 0;
  let wF = 0;
  let maxVariance = 0;
  let threshold = 0;

  for (let i = 0; i < 256; i++) {
    wB += histogram[i];
    if (wB === 0) continue;

    wF = total - wB;
    if (wF === 0) break;

    sumB += i * histogram[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;

    const variance = wB * wF * (mB - mF) ** 2;
    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = i;
    }
  }

  return threshold;
};

/**
 * Binarized an image using Otsu's method
 *
 * @param {Image1Channel} image1Channel - The image data
 * @returns {Image1Channel} the binarized image data
 */
export const globalOtsuThreshold = (image1Channel) => {
  const histogram = new Array(256).fill(0);
  for (let i = 0; i < image1Channel.numPixels; i++) {
    histogram[image1Channel.data[i]]++;
  }

  const threshold = otsuThreshold(histogram);
  const binarizedData = image1Channel.data.map((luminance) =>
    luminance < threshold ? 255 : 0,
  );

  return new Image1Channel(
    binarizedData,
    image1Channel.width,
    image1Channel.height,
  );
};

/**
 * Otsu's method is used to find the threshold value for binarization
 * @param {number} kernelSize - The kernel size, the size of the kernel to apply the filter
 * @returns {function(Image1Channel): Image1Channel} The Otsu threshold filter function
 */
export const createOtsuThresholdFilter = (kernelSize) => (image1Channel) => {
  const width = image1Channel.width;
  const height = image1Channel.height;

  // calculate the number of columns and rows of the kernel
  const columns = Math.ceil(width / kernelSize);
  const rows = Math.ceil(height / kernelSize);

  const kernels = new Array(columns * rows).fill(0).map((_, index) => {
    const x = index % columns;
    const y = Math.floor(index / columns);

    const startX = x * kernelSize;
    const startY = y * kernelSize;
    const endX = Math.min(startX + kernelSize, width);
    const endY = Math.min(startY + kernelSize, height);

    const pixelCounts = (endX - startX) * (endY - startY);
    const histogram = new Array(256).fill(0);

    let min = 255;
    let max = 0;
    for (let i = startY; i < endY; i++) {
      for (let j = startX; j < endX; j++) {
        const pixelIndex = i * width + j;
        const luminance = image1Channel.data[pixelIndex];
        histogram[luminance]++;

        if (luminance < min) {
          min = luminance;
        }
        if (luminance > max) {
          max = luminance;
        }
      }
    }

    return {
      histogram: histogram.map((count) => count / pixelCounts),
      min,
      max,
    };
  });

  // doing correction for each kernel if the luminicance range is less than the threshold
  const neighbourSmoothedKernel = kernels.map((kernel, index) => {
    const x = index % columns;
    const y = Math.floor(index / columns);

    // implementing a greedy approach to find the optimal threshold
    // starts by only the kernel itself
    let maxRange = kernel.max - kernel.min;
    if (maxRange > 24) {
      return otsuThreshold(kernel.histogram);
    }

    let includedKernels = [kernel];
    for (let i = 0; i < 5; i++) {
      // add more neigbour kernels if the range is still less than 24

      const layerNeighbours = manhattanNeighbors(0, 0, i).map(([dx, dy]) => {
        return { kernel: kernels[x + dx + (y + dy) * columns], weight: 1 };
      });

      for (const neighbour of layerNeighbours.filter(
        (neighbour) => neighbour.kernel !== undefined,
      )) {
        const range = neighbour.kernel.max - neighbour.kernel.min;
        if (range > maxRange) {
          maxRange = range;
        }
        includedKernels.push(neighbour.kernel);
      }

      if (maxRange > 24) {
        const newHistogram = includedKernels.reduce(
          (acc, kernel) => {
            for (let i = 0; i < 256; i++) {
              acc.val[i] += kernel.histogram[i];
            }
            acc.weight += 1;
            return acc;
          },
          { val: new Array(256).fill(0), weight: 0 },
        );

        return otsuThreshold(newHistogram.val);
      }

      const probabilities = neighbours
        .filter((neighbour) => neighbour.kernel !== undefined)
        .reduce(
          (acc, neighbour) => {
            for (let i = 0; i < 256; i++) {
              acc.val[i] += neighbour.kernel[i] * neighbour.weight;
            }

            acc.weight += neighbour.weight;
            return acc;
          },
          { val: new Array(256).fill(0), weight: 0 },
        );

      const combinedProbabilities = probabilities.val.map(
        (val) => val / probabilities.weight,
      );

      // Step 2: Compute cumulative sums and cumulative means
      let wB = 0,
        wF = 0;
      let sumB = 0,
        sumTotal = 0;
      let maxVariance = 0,
        optimalThreshold = 0;

      // Precompute sum of all pixel intensities
      for (let i = 0; i < 256; i++) {
        sumTotal += i * combinedProbabilities[i]; // Total mean * probabilities
      }

      // Step 3: Loop through all possible thresholds to find the best one
      for (let t = 0; t < 256; t++) {
        wB += combinedProbabilities[t]; // Update background weight
        if (wB === 0) continue; // Avoid division by zero

        wF = 1 - wB; // Foreground weight
        if (wF === 0) break; // No pixels left in foreground

        sumB += t * combinedProbabilities[t]; // Update background mean numerator
        let muB = sumB / wB; // Background mean
        let muF = (sumTotal - sumB) / wF; // Foreground mean

        // Compute inter-class variance
        let variance = wB * wF * (muB - muF) ** 2;

        // Update the best threshold
        if (variance > maxVariance) {
          maxVariance = variance;
          optimalThreshold = t;
        }
      }

      return optimalThreshold;
    }
  });

  const binarizedData = image1Channel.data.map((luminance, index) => {
    const x = index % width;
    const y = Math.floor(index / width);

    const kernelX = Math.floor(x / kernelSize);
    const kernelY = Math.floor(y / kernelSize);
    const kernelIndex = kernelX + kernelY * columns;

    const threshold = neighbourSmoothedKernel[kernelIndex];
    return luminance < threshold ? 255 : 0;
  });

  // Step 4: Binarize the image
  return new Image1Channel(
    binarizedData,
    image1Channel.width,
    image1Channel.height,
  );
};

/**
 * create a binary filter to apply to greyscale image
 * @param {number} threshold - The threshold value, the minimal range to start binarization, pixels with variant smaller than this will all be set to 0
 * @param {number} kernelSize - The kernel size, the size of the kernel to apply the filter
 * @returns {function(Image1Channel): Image1Channel} The binary filter function
 */
export const createBinaryFilter =
  (threshold, kernelSize) => (image1Channel) => {
    const width = image1Channel.width;
    const height = image1Channel.height;

    // calculate the number of columns and rows of the kernel
    const columns = Math.ceil(width / kernelSize);
    const rows = Math.ceil(height / kernelSize);
    const kernels = new Array(columns * rows).fill(0).map((_, index) => {
      const x = index % columns;
      const y = Math.floor(index / columns);

      const startX = x * kernelSize;
      const startY = y * kernelSize;
      const endX = Math.min(startX + kernelSize, width);
      const endY = Math.min(startY + kernelSize, height);

      const pixelCounts = (endX - startX) * (endY - startY);
      let maxLuminance = 0;
      let minLuminance = 255;
      let sumLuminance = 0;

      for (let i = startY; i < endY; i++) {
        for (let j = startX; j < endX; j++) {
          const pixelIndex = i * width + j;

          maxLuminance = Math.max(maxLuminance, image1Channel.data[pixelIndex]);
          minLuminance = Math.min(minLuminance, image1Channel.data[pixelIndex]);
          sumLuminance += image1Channel.data[pixelIndex];
        }
      }

      const avgLuminance = sumLuminance / pixelCounts;
      if (maxLuminance - minLuminance < threshold) {
        // if the luminance range is less than the threshold, we will use the minLuminance as the threshold
        return { maxLuminance, minLuminance, avgLuminance: minLuminance / 2 };
      }
      return { maxLuminance, minLuminance, avgLuminance };
    });

    // doing correction for each kernel if the luminicance range is less than the threshold
    const neighbourSmoothedKernel = kernels.map((kernel, index) => {
      const range = kernel.maxLuminance - kernel.minLuminance;
      if (range < threshold) {
        const x = index % columns;
        const y = Math.floor(index / columns);

        const neighbours = [
          { kernel: kernels[x - 1 + (y - 1) * columns], weight: 1 }, // top left
          { kernel: kernels[x + (y - 1) * columns], weight: 2 }, // top
          { kernel: kernels[x + 1 + (y - 1) * columns], weight: 1 }, // top right
          { kernel: kernels[x - 1 + y * columns], weight: 2 }, // left
          { kernel: kernels[x + 1 + y * columns], weight: 2 }, // right
          { kernel: kernels[x - 1 + (y + 1) * columns], weight: 1 }, // bottom left
          { kernel: kernels[x + (y + 1) * columns], weight: 2 }, // bottom
          { kernel: kernels[x + 1 + (y + 1) * columns], weight: 1 }, // bottom right
        ];

        const weightedAverage = neighbours
          .filter((neighbour) => neighbour.kernel !== undefined)
          .reduce(
            (acc, neighbour) => {
              acc.sum += neighbour.kernel.avgLuminance * neighbour.weight;
              acc.weight += neighbour.weight;
              return acc;
            },
            { sum: 0, weight: 0 },
          );

        const avg = weightedAverage.sum / weightedAverage.weight;
        if (kernel.minLuminance < avg) {
          return { avgLuminance: avg };
        }

        return { avgLuminance: kernel.avgLuminance };
      }

      return { avgLuminance: kernel.avgLuminance };
    });

    const smoothKernels = neighbourSmoothedKernel.map((kernel, index) => {
      const x = index % columns;
      const y = Math.floor(index / columns);

      let neighbours = [kernel];
      for (let i = -2; i <= 2; i++) {
        for (let j = -2; j <= 2; j++) {
          const neighbourX = x + i;
          const neighbourY = y + j;
          const neighbour =
            neighbourSmoothedKernel[neighbourX + neighbourY * columns];
          if (neighbour !== undefined) {
            neighbours.push(neighbour);
          }
        }
      }
      const sumLuminance = neighbours.reduce(
        (acc, neighbour) => acc + neighbour.avgLuminance,
        0,
      );
      const threshold = sumLuminance / neighbours.length;
      return { avgLuminance: threshold };
    });

    const binarizedData = image1Channel.data.map((luminance, index) => {
      const x = index % width;
      const y = Math.floor(index / width);

      const kernelX = Math.floor(x / kernelSize);
      const kernelY = Math.floor(y / kernelSize);
      const kernelIndex = kernelX + kernelY * columns;

      const kernel = smoothKernels[kernelIndex];
      return luminance < kernel.avgLuminance ? 255 : 0;
    });

    return new Image1Channel(binarizedData, width, height);
  };

class Matrix {
  /** @type {Uint8Array} */
  #data;

  /** @type {number} */
  #width;

  /** @type {number} */
  #height;


  constructor(width, height){

    this.#width = width;
    this.#height = height;
    this.#data = new Uint8Array(width * height);
  }

  get width(){
    return this.#width;
  }

  get height(){
    return this.#height;
  }

  /**
    * @param {number} x
    * @param {number} y
    */
  get(x, y){

    return this.#data[y * this.#width + x];
  }

  /**
    * @param {number} x
    * @param {number} y
    * @param {number} value
    */
  set(x, y, value){

    return this.#data[y * this.#width + x] = value;
  }
}

/**
  * @param {number} value
  * @param {number} min
  * @param {number} max
  * @returns {number}
  *
  */
function numBetween(value, min, max) {
  return value < min ? min : value > max ? max : value;
}

const REGION_SIZE = 8;
const MIN_DYNAMIC_RANGE = 24;
/**
  * @param {Image1Channel} greyScaleImage
  */
export function jsqrBinarize(greyScaleImage) {

  const width = greyScaleImage.width;
  const height = greyScaleImage.height;
  const pixelCount = width * height;
  const horizontalRegionCount = Math.ceil(width / REGION_SIZE);
  const verticalRegionCount = Math.ceil(height / REGION_SIZE);
  
  const blackPoints = new Matrix(horizontalRegionCount, verticalRegionCount);
  for (let verticalRegion = 0; verticalRegion < verticalRegionCount; verticalRegion++) {
    for (let hortizontalRegion = 0; hortizontalRegion < horizontalRegionCount; hortizontalRegion++) {
      let min = Infinity;
      let max = 0;
      for (let y = 0; y < REGION_SIZE; y++) {
        for (let x = 0; x < REGION_SIZE; x++) {
          // const pixelLumosity =
          //   greyscalePixels.get(hortizontalRegion * REGION_SIZE + x, verticalRegion * REGION_SIZE + y);
	  const yIndex = hortizontalRegion * REGION_SIZE;
	  const xIndex = verticalRegion * REGION_SIZE + y;
	  const pixelLumosity = greyScaleImage.data[yIndex * width + xIndex];
          min = Math.min(min, pixelLumosity);
          max = Math.max(max, pixelLumosity);
        }
      }
      // We could also compute the real average of all pixels but following the assumption that the qr code consists
      // of bright and dark pixels and essentially not much in between, by (min + max)/2 we make the cut really between
      // those two classes. If using the average over all pixel in a block of mostly bright pixels and few dark pixels,
      // the avg would tend to the bright side and darker bright pixels could be interpreted as dark.
      let average = (min + max) / 2;
      // Small bias towards black by moving the threshold up. We do this, as in the finder patterns white holes tend
      // to appear which makes them undetectable.
      const blackBias = 1.11;
      average = Math.min(255, average * blackBias);
      if (max - min <= MIN_DYNAMIC_RANGE) {
        // If variation within the block is low, assume this is a block with only light or only
        // dark pixels. In that case we do not want to use the average, as it would divide this
        // low contrast area into black and white pixels, essentially creating data out of noise.
        //
        // Default the blackpoint for these blocks to be half the min - effectively white them out
        average = min / 2;

        if (verticalRegion > 0 && hortizontalRegion > 0) {
          // Correct the "white background" assumption for blocks that have neighbors by comparing
          // the pixels in this block to the previously calculated black points. This is based on
          // the fact that dark barcode symbology is always surrounded by some amount of light
          // background for which reasonable black point estimates were made. The bp estimated at
          // the boundaries is used for the interior.

          // The (min < bp) is arbitrary but works better than other heuristics that were tried.
          const averageNeighborBlackPoint = (
            blackPoints.get(hortizontalRegion, verticalRegion - 1) +
            (2 * blackPoints.get(hortizontalRegion - 1, verticalRegion)) +
            blackPoints.get(hortizontalRegion - 1, verticalRegion - 1)
          ) / 4;
          if (min < averageNeighborBlackPoint) {
            average = averageNeighborBlackPoint; // no need to apply black bias as already applied to neighbors
          }
        }
      }
      blackPoints.set(hortizontalRegion, verticalRegion, average);
    }
  }

  let binarizedArr = new Uint8Array(pixelCount);

  for (let verticalRegion = 0; verticalRegion < verticalRegionCount; verticalRegion++) {
    for (let hortizontalRegion = 0; hortizontalRegion < horizontalRegionCount; hortizontalRegion++) {
      const left = numBetween(hortizontalRegion, 2, horizontalRegionCount - 3);
      const top = numBetween(verticalRegion, 2, verticalRegionCount - 3);
      let sum = 0;
      for (let xRegion = -2; xRegion <= 2; xRegion++) {
        for (let yRegion = -2; yRegion <= 2; yRegion++) {
          sum += blackPoints.get(left + xRegion, top + yRegion);
        }
      }
      const threshold = sum / 25;
      for (let xRegion = 0; xRegion < REGION_SIZE; xRegion++) {
        for (let yRegion = 0; yRegion < REGION_SIZE; yRegion++) {
          const x = hortizontalRegion * REGION_SIZE + xRegion;
          const y = verticalRegion * REGION_SIZE + yRegion;
	  const lum = greyScaleImage.data[y * width + x];

          binarizedArr[ y * width + x ] = lum <= threshold ? 255 : 0;
        }
      }
    }
  }
  const binarized = new Image1Channel(binarizedArr, width, height);
  return binarized;
}

